import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight } from "lucide-react";
import { AREAS } from "@/data/site";

/**
 * Article "Areas we serve" internal-SEO backlink widget.
 * Shown at the end of every blog article to strengthen the site's
 * internal linking graph for local Surrey / Sussex search terms.
 *
 * We surface the most commercially relevant Surrey / Gatwick areas
 * first, then any areas whose names appear in the article body
 * (topical relevance), capped at 8 links so the card stays scannable.
 */
const CORE_ORDER = [
  "crawley", "horley", "reigate", "redhill", "horsham",
  "east-grinstead", "haywards-heath", "three-bridges",
];

export const ArticleAreasWidget = ({ articleBody = "" }) => {
  const bodyLower = (articleBody || "").toLowerCase();
  const mentioned = AREAS.filter((a) => bodyLower.includes(a.name.toLowerCase()));
  const core = CORE_ORDER
    .map((slug) => AREAS.find((a) => a.slug === slug))
    .filter(Boolean);

  const seen = new Set();
  const picks = [];
  for (const a of [...mentioned, ...core, ...AREAS]) {
    if (seen.has(a.slug)) continue;
    seen.add(a.slug);
    picks.push(a);
    if (picks.length === 8) break;
  }

  return (
    <aside data-testid="article-areas-widget" className="mt-10 sm:mt-12 rounded-2xl border border-[#EAEBEA] bg-surface p-6 sm:p-7 not-prose">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand mb-1">Areas we serve</p>
          <h4 className="font-heading text-lg sm:text-xl font-bold text-ink">Gatwick transfers across Surrey &amp; Sussex</h4>
        </div>
        <Link to="/#areas" className="hidden sm:inline text-xs font-semibold text-ink/50 hover:text-brand transition-colors">All areas →</Link>
      </div>
      <p className="text-sm text-ink/60 mb-5 leading-relaxed">
        Whichever town you're travelling from, our licensed drivers know every route to Gatwick North and South Terminals. Tap your local area for fixed-price fares and route details.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
        {picks.map((a) => (
          <Link
            key={a.slug}
            to={`/areas/${a.slug}`}
            data-testid={`article-area-${a.slug}`}
            className="group flex items-center justify-between gap-2 bg-white border border-[#EAEBEA] hover:border-brand rounded-lg px-3.5 py-2.5 transition-colors"
          >
            <span className="flex items-center gap-2 text-sm text-ink group-hover:text-brand transition-colors truncate">
              <MapPin className="h-3.5 w-3.5 text-brand shrink-0" strokeWidth={2} />
              <span className="truncate">{a.name}</span>
            </span>
            <ArrowUpRight className="h-3.5 w-3.5 text-ink/30 group-hover:text-brand group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0" />
          </Link>
        ))}
      </div>
    </aside>
  );
};
